/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What the plan tab is allowed to claim about consumption.
 *
 * Only the quotas the catalogue actually meters appear: seats, agent
 * interactions and partnership searches. Properties are counted but have no
 * ceiling in any plan, and there is no portal-listing quota at all — a bar
 * drawn against an invented denominator is worse than no bar, because it
 * reads as a limit someone can hit.
 */

import type { TFunction } from 'i18next';
import type { PlanQuota } from '@/shared/ui/components/composed/settings-block';
import { isQuotaUnderPressure } from '@/shared/ui/components/composed/settings-block';
import type { PlanWithFrequency } from '@features/onboarding/hooks/use-plans';
import type { SeatUsageSummary } from '@services/requests/housien-services/AgenciesService.subscription';

/** The subscription facts the quota builder needs. */
export interface QuotaSubscriptionInput {
  plan: { max_seats?: number | null } | null;
}

/**
 * Builds the metered quotas for the plan block.
 *
 * A quota whose ceiling the plan does not report is still shown, as a count
 * without a track — "you have 5 people" is true and useful; "5 of ?" is not.
 *
 * @param input - Subscription detail, usage summary and seat occupancy.
 * @param t - Translation function.
 * @returns The quotas, in display order.
 */
export function buildPlanQuotas(
  input: {
    subscription: QuotaSubscriptionInput | null;
    usage: SeatUsageSummary | null;
    memberCount: number;
    /** True for the solo-broker segment, whose plans are all single-seat. */
    isSoloBroker?: boolean;
  },
  t: TFunction,
): PlanQuota[] {
  const { subscription, usage, memberCount, isSoloBroker = false } = input;
  // A solo broker's plan is one seat by definition, and the broker occupies
  // it. Metering that reports a permanent "1 de 1 — sem vagas" in alarm red,
  // and drives the upgrade block from it, describes the plan working as sold
  // as though it were a problem to fix.
  const quotas: PlanQuota[] = isSoloBroker ? [] : [{
    key: 'seats',
    label: t('subscription.usage.seats'),
    used: memberCount,
    total: subscription?.plan?.max_seats ?? null,
  }];
  // Seats are a standing allowance, not a monthly one: they do not refill at
  // the start of a period, so they never "run out for this period".
  const STANDING = new Set(['seats']);

  if (usage) {
    quotas.push({
      key: 'interaction',
      label: t('subscription.usage.interactions'),
      used: usage.interaction.used,
      total: usage.interaction.total,
    });
    quotas.push({
      key: 'partnershipSearch',
      label: t('subscription.usage.partnershipSearches'),
      used: usage.partnership_search.used,
      total: usage.partnership_search.total,
    });
  }

  return quotas.map((quota) => ({
    ...quota,
    // "5 de 10", as the prototype reads it — a slash reads as a fraction and
    // invites the eye to divide rather than to compare.
    countLabel: quota.total === null
      ? String(quota.used)
      : t('subscription.usage.count', { used: quota.used, total: quota.total }),
    caption: buildQuotaCaption(quota, STANDING.has(quota.key), t),
  }));
}

/**
 * The line under a quota bar: what is left, or that there is none left.
 *
 * A standing allowance that is full says so in the present tense — "no seats
 * left" — because it will not refill on its own. A period allowance names the
 * period, because waiting is a real option there and it is not here.
 *
 * @param quota - The quota to describe.
 * @param isStanding - Whether the allowance persists rather than refilling.
 * @param t - Translation function.
 * @returns The caption, or undefined when the quota has no ceiling.
 */
function buildQuotaCaption(quota: PlanQuota, isStanding: boolean, t: TFunction): string | undefined {
  if (quota.total === null) return undefined;
  const remaining = Math.max(quota.total - quota.used, 0);
  if (remaining > 0) {
    return isStanding
      ? t('subscription.usage.seatsFree', { count: remaining })
      : t('subscription.usage.remaining', { count: remaining });
  }
  return isStanding
    ? t('subscription.usage.standingFull', { over: Math.max(quota.used - quota.total, 0) })
    : t('subscription.usage.exhausted');
}

/**
 * The quotas that are close enough to their ceiling to justify an offer.
 *
 * @param quotas - The plan's quotas.
 * @returns The pressured quotas, in display order.
 */
export function pressuredQuotas(quotas: PlanQuota[]): PlanQuota[] {
  return quotas.filter(isQuotaUnderPressure);
}

/**
 * Finds the cheapest plan strictly above what the agency pays today.
 *
 * Returns null when the current price is unknown — Payien not reporting a
 * price would otherwise make every plan look like an upgrade, which is how a
 * warning turns into a billboard.
 *
 * @param plans - The plans available for the agency's segment.
 * @param currentPriceCents - What the agency pays today, or null.
 * @returns The upgrade target, or null when there is nothing above.
 */
export function findUpgradeTarget(
  plans: PlanWithFrequency[],
  currentPriceCents: number | null,
): PlanWithFrequency | null {
  if (currentPriceCents === null) return null;
  const above = plans
    .filter((plan) => plan.price * 100 > currentPriceCents)
    .sort((a, b) => a.price - b.price);
  return above[0] ?? null;
}

/**
 * Whether the dark upgrade block has earned its place on the screen.
 *
 * Both conditions are required: something above to move to, and a quota the
 * agency is actually running out of. Against a comfortable account the same
 * block is an advertisement, and the sober treatment of every other tab stops
 * being believed.
 *
 * @param quotas - The plan's quotas.
 * @param target - The upgrade target, or null.
 * @returns True when the block should render.
 */
export function shouldOfferUpgrade(quotas: PlanQuota[], target: PlanWithFrequency | null): boolean {
  return target !== null && pressuredQuotas(quotas).length > 0;
}
